import { useMemo, useState, useCallback } from 'react';

/**
 * Build a tree from a flat category list using `parent` field.
 * Returns top-level nodes, each with a `children` array.
 */
function buildTree(categories) {
  const map = {};
  const roots = [];

  categories.forEach((cat) => {
    map[cat.id] = { ...cat, children: [] };
  });

  categories.forEach((cat) => {
    const node = map[cat.id];
    if (cat.parent && map[cat.parent]) {
      map[cat.parent].children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/**
 * Check whether a node or any of its descendants matches the selected slug.
 */
function treeContainsSlug(node, slug) {
  if (node.slug === slug) return true;
  return node.children.some((child) => treeContainsSlug(child, slug));
}

/**
 * Single category button.
 */
function CategoryItem({ cat, selected, onSelect, depth = 0 }) {
  const isActive = selected === cat.slug;
  const hasChildren = cat.children.length > 0;
  const childSelected = hasChildren && treeContainsSlug(cat, selected);
  const [expanded, setExpanded] = useState(childSelected);

  // Auto-expand when a child becomes selected
  const isExpanded = expanded || childSelected;

  const handleClick = useCallback(() => {
    onSelect(cat.slug);
    if (hasChildren) setExpanded(true);
  }, [cat.slug, hasChildren, onSelect]);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  }, []);

  return (
    <>
      <div className="flex items-center">
        {/* Expand/collapse toggle for parents */}
        {hasChildren ? (
          <button
            type="button"
            onClick={handleToggle}
            className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ) : (
          <span className="w-5 flex-shrink-0" />
        )}

        <button
          type="button"
          role="tab"
          aria-selected={isActive}
          className={`flex-1 text-left rounded-lg px-2 py-1.5 text-sm font-medium transition-colors truncate ${
            isActive
              ? 'bg-pos-primary text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
          onClick={handleClick}
        >
          {cat.name}
          {typeof cat.count === 'number' && (
            <span className={`ml-1 text-xs ${isActive ? 'text-white/70' : 'text-gray-400'}`}>
              {cat.count}
            </span>
          )}
        </button>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="ml-3 border-l border-gray-200 pl-1 flex flex-col gap-0.5">
          {cat.children.map((child) => (
            <CategoryItem
              key={child.slug}
              cat={child}
              selected={selected}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * CategoryFilter component for filtering products by category.
 *
 * Renders a vertical scrollable list with nested subcategories.
 *
 * @param {Object}   props
 * @param {Array}    props.categories - Array of {id, name, slug, count, parent}.
 * @param {string}   props.selected   - Currently selected category slug, or '' for all.
 * @param {Function} props.onSelect   - Callback fired when a category is clicked.
 */
export default function CategoryFilter({ categories = [], selected = '', onSelect }) {
  const tree = useMemo(() => buildTree(categories), [categories]);

  return (
    <nav
      className="flex flex-col gap-0.5 overflow-y-auto py-3 px-2"
      role="tablist"
      aria-label="Product categories"
    >
      {/* "All" tab */}
      <div className="flex items-center">
        <span className="w-5 flex-shrink-0" />
        <button
          type="button"
          role="tab"
          aria-selected={selected === ''}
          className={`flex-1 text-left rounded-lg px-2 py-1.5 text-sm font-medium transition-colors ${
            selected === ''
              ? 'bg-pos-primary text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
          onClick={() => onSelect('')}
        >
          All
        </button>
      </div>

      {tree.map((cat) => (
        <CategoryItem
          key={cat.slug}
          cat={cat}
          selected={selected}
          onSelect={onSelect}
        />
      ))}
    </nav>
  );
}
